import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, CheckCircle2, ArrowRight } from 'lucide-react';
import { mySurveyProgress } from '@/lib/pulse/surveyApi';
import { cn } from '@/lib/utils';

interface Props {
  orgId: string;
}

/**
 * Persistent banner shown across all employee pages while a survey is active.
 * Gives constant visual presence with topic, live progress, and a CTA.
 */
export function ActiveSurveyBanner({ orgId }: Props) {
  const location = useLocation();

  const { data } = useQuery({
    queryKey: ['my_survey_progress', orgId],
    queryFn: async () => {
      const { data, error } = await mySurveyProgress(orgId);
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  if (!data || data.active !== true) return null;

  // Don't show on the survey page itself — would be redundant
  if (location.pathname.startsWith('/app/employee/survey')) return null;

  const { survey_topic, answered, total_anchors } = data;
  const pct = total_anchors > 0 ? Math.round((answered / total_anchors) * 100) : 0;
  const done = answered >= total_anchors && total_anchors > 0;

  return (
    <Link
      to="/app/employee/survey"
      className={cn(
        'group block border-b border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10',
        'hover:from-primary/15 hover:via-primary/10 hover:to-primary/15 transition-colors',
      )}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <div className="relative shrink-0">
          {done ? (
            <CheckCircle2 className="h-5 w-5 text-primary" />
          ) : (
            <>
              <ClipboardList className="h-5 w-5 text-primary" />
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary animate-pulse" />
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground truncate">
              {survey_topic}
            </span>
            <span className="text-xs text-muted-foreground">
              {done
                ? 'All answered'
                : answered === 0
                  ? `${total_anchors} quick questions`
                  : `${answered} of ${total_anchors} done`}
            </span>
          </div>
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-primary/15">
            <div
              className="h-full bg-primary transition-[width] duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-primary shrink-0">
          {done ? 'Review' : answered === 0 ? 'Start' : 'Continue'}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}
